import { useState, useRef, useEffect } from 'react'

import DataHubService from '@space-lib/datahub/hotmartClub/dataHubService'
import { useManageModal } from '@space-lib/hooks'
import { getCookieCloseBanner } from '@space-shell/common/components/pwaBar/utils'
import {
  ClickPwaBannerDownloadSchema,
  ClickPwaBannerDownloadConfirmSchema
} from '@space-shell/modules/home/datahub/schemas/click/pwa'
import { ClosePwaBannerDownloadSchema } from '@space-shell/modules/home/datahub/schemas/close/pwa'
import {
  ViewPwaBannerSchema,
  ViewPwaModalDownloadSchema
} from '@space-shell/modules/home/datahub/schemas/view/pwa'

import type {
  BeforeInstallPromptEvent,
  UseBannerPwaInstallParams,
  UseBannerPwaInstallReturn
} from '../types'

import { handleArrivedFrom, setCookieCloseBanner } from '../utils'
const TIMEOUT = 3000
const MAX_RETRY_COUNT = 10
const RETRY_COUNT_TIME = 8000
/**
 * Hook to handle PWA banner exhibition and decide which approach to use: the default browser banner or an iOS modal.
 *
 * @param {boolean} shouldUseIOSModal - Determines whether to show the iOS modal or the default browser banner.
 * @param {string} language - The locale language from the URL.
 * @param {object} membershipData - An object containing membership values.
 *
 */
const useBannerPwaInstall = ({
  shouldUseIOSModal,
  language,
  membershipData
}: UseBannerPwaInstallParams): UseBannerPwaInstallReturn => {
  const [retryCount, setRetryCount] = useState<number>(0)
  const [isBannerClosed, setIsBannerClosed] = useState<boolean>(true)
  const { isModalOpen, handleOpen, handleClose } = useManageModal()
  const promptRef = useRef<BeforeInstallPromptEvent | null>(null)

  const clickPwaBannerDownloadSchema = new ClickPwaBannerDownloadSchema()

  const viewPwaModalDownloadSchema = new ViewPwaModalDownloadSchema({
    arrivedFrom: handleArrivedFrom({ language, slug: membershipData?.slug })
  })
  const closePwaBannerDownloadSchema = new ClosePwaBannerDownloadSchema()

  useEffect(() => {
    const clickPwaBannerDownloadConfirmSchema = new ClickPwaBannerDownloadConfirmSchema()
    const viewPwaBannerSchema = new ViewPwaBannerSchema({
      arrivedFrom: handleArrivedFrom({ language, slug: membershipData?.slug })
    })

    const handlePwaInstallEvent = () => {
      DataHubService.sendEvent({ schema: clickPwaBannerDownloadConfirmSchema })
    }
    window.addEventListener('appinstalled', handlePwaInstallEvent)

    setTimeout(() => {
      if (!shouldUseIOSModal && window?.pwa_banner) {
        promptRef.current = window?.pwa_banner
        setIsBannerClosed(false)
        DataHubService.sendEvent({ schema: viewPwaBannerSchema })
      }

      if (shouldUseIOSModal) {
        setIsBannerClosed(false)
        DataHubService.sendEvent({ schema: viewPwaBannerSchema })
      }
    }, TIMEOUT)

    return () => {
      promptRef.current = null
      window.removeEventListener('appinstalled', handlePwaInstallEvent)
    }
  }, [shouldUseIOSModal, language, membershipData])

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null
    const hasHideBannerCookie = getCookieCloseBanner(membershipData?.slug || '') || false
    const canRetry = isBannerClosed && !hasHideBannerCookie && retryCount <= MAX_RETRY_COUNT

    if (canRetry) {
      timer = setTimeout(() => {
        setRetryCount((prev) => prev + 1)
      }, RETRY_COUNT_TIME)
    }

    return () => {
      timer && clearTimeout(timer)
    }
  }, [isBannerClosed, retryCount, membershipData])

  const callBrowserDefaultPwaBanner = async () => {
    if (promptRef.current) {
      DataHubService.sendEvent({ schema: clickPwaBannerDownloadSchema })
      promptRef.current.prompt()
      const { outcome } = await promptRef.current.userChoice
      if (outcome === 'accepted') {
        setIsBannerClosed(true)
      }
      promptRef.current = null
    } else {
      setIsBannerClosed(true)
    }
  }

  const callIOSModalPwaBanner = () => {
    DataHubService.sendEvent({ schema: clickPwaBannerDownloadSchema })
    DataHubService.sendEvent({ schema: viewPwaModalDownloadSchema })
    handleOpen()
  }

  const handleCloseBanner = () => {
    setIsBannerClosed(true)
    setCookieCloseBanner(membershipData!.slug)
    DataHubService.sendEvent({ schema: closePwaBannerDownloadSchema })
  }

  return {
    isBannerClosed,
    setIsBannerClosed: handleCloseBanner,
    isModalOpen,
    setIsModalClosed: handleClose,
    callBrowserDefaultPwaBanner,
    callIOSModalPwaBanner
  }
}

export default useBannerPwaInstall
